/**
 * EditFormV1 — Generic reusable edit form component
 *
 * Works for any module (jobs, candidates, submissions, etc.)
 * All module-specific behaviour is passed in via props — zero hardcoding inside.
 *
 * Props:
 *   moduleName      string    — e.g. "job", "candidate"
 *   recordId        string    — MongoDB _id of the record
 *   breadcrumbItems array     — passed to AppBreadcrumb
 *   submitText      string    — Save button label (default "Save Changes")
 *   cancelPath      string    — navigate here on cancel/success
 *   onSuccess       function  — called after successful save (overrides cancelPath)
 *   fieldNameMap    object    — rename form fields before sending to API
 *                              e.g. { mspReqId: 'mspreqId' }
 *   onFileUpload    function  — (fieldName, file, recordId) => Promise
 *                              handle multipart uploads per module
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Form,
    Input,
    Radio,
    Row,
    Select,
    Space,
    Spin,
    Switch,
    TimePicker,
    Tooltip,
    Typography,
    Upload,
    message,
} from 'antd';
import {
    CalendarOutlined,
    CloudUploadOutlined,
    DeleteOutlined,
    DownOutlined,
    EnvironmentOutlined,
    EyeOutlined,
    FileExcelOutlined,
    FileImageOutlined,
    FileOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    FileWordOutlined,
    InfoCircleOutlined,
    MailOutlined,
    PhoneOutlined,
    PlusOutlined,
    SolutionOutlined,
    TagsOutlined,
    UpOutlined,
    UserOutlined,
} from '@ant-design/icons';
import '../styles/AddFormV1.css';
import AppBreadcrumb from './Breadcrumb';
import TipTapEditor from './TipTapEditor';
import { useNavigate } from 'react-router-dom';
import { fetchJsonWithAuth } from '../services/authApi';
import { AUTH_URL, JOBS_URL } from '../services/apiConfig';
import { getDropdownValues } from '../services/moduleDataApi';
import { getFormGroups } from '../services/addFormApi';
import { updateRecord } from '../services/detailedViewApi';
import {
    buildPayload as buildConfigPayload,
    buildInitialValue,
    collectFileParts,
    effectiveDataType,
    getDeep,
} from './form/payloadTransformer';
import { getEditFormFallbackOptions } from './form/editFormOptionFallbacks';

const { Text } = Typography;
const { TextArea } = Input;


const sectionIcons = [
    <UserOutlined />,
    <EnvironmentOutlined />,
    <SolutionOutlined />,
    <InfoCircleOutlined />,
    <FileTextOutlined />,
];

const fieldIcons = {
    email: <MailOutlined className="v1-input-icon" />,
    contactNumber: <PhoneOutlined className="v1-input-icon" />,
    phone: <PhoneOutlined className="v1-input-icon" />,
    currentLocation: <EnvironmentOutlined className="v1-input-icon" />,
    preferredLocation: <EnvironmentOutlined className="v1-input-icon" />,
    designation: <SolutionOutlined className="v1-input-icon" />,
    skills: <TagsOutlined className="v1-input-icon" />,
    firstName: <UserOutlined className="v1-input-icon" />,
    lastName: <UserOutlined className="v1-input-icon" />,
    jobTitle: <SolutionOutlined className="v1-input-icon" />,
    jobLocation: <EnvironmentOutlined className="v1-input-icon" />,
    vendorEmail: <MailOutlined className="v1-input-icon" />,
};

// ─── Pure helpers ────────────────────────────────────────────────────────────

const truthy = (v) => v === true || v === 1 || v === '1';
const editable = (field) => field?.edit === undefined || truthy(field.edit);
const empty = (v) => v === undefined || v === null || v === '';

function normalizeModule(name = '') {
    const n = String(name).toLowerCase();
    if (n === 'job') return 'jobs';
    if (n === 'candidate') return 'candidates';
    if (n === 'submissions') return 'submission';
    return n;
}

function normalizeOptions(options = []) {
    return options.map((o) => {
        if (typeof o === 'string') return { label: o, value: o };
        return {
            label: o.label ?? o.name ?? o.value ?? o.userName ?? o.email,
            value: o.value ?? o.id ?? o.userId ?? o._id ?? o.name ?? o.label,
        };
    });
}

function FieldLabel({ field, label }) {
    if (!label) return label;
    if (!truthy(field?.infoEnabled) || empty(field?.infoText)) return label;
    return (
        <span className="v1-field-label-with-info">
            <span>{label}</span>
            <Tooltip title={field.infoText}>
                <InfoCircleOutlined className="v1-field-info-icon" />
            </Tooltip>
        </span>
    );
}

function GroupInfoMessage({ group }) {
    if (!truthy(group?.infoEnabled) || empty(group?.infoText)) return null;
    return <Text type="secondary" className="v1-group-info-message">{group.infoText}</Text>;
}

function normalizeGroups(groups = []) {
    return [...groups]
        .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
        .map((g) => ({
            ...g,
            fields: [...(g.fields ?? [])]
                .filter((f) => f.show === undefined || truthy(f.show))
                .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)),
        }))
        .filter((g) => g.fields.length);
}

// fileListFromValue — turn a file field's embedded value (from getFormGroups) into
// the antd fileList the file control expects. The value may be a full document
// object ({ location, uploadName/name, uniqueName }), a bare original filename
// string (e.g. "Vitest.pdf"), or an array of either. Returns undefined when empty.
function fileListFromValue(stored, field) {
    const list = Array.isArray(stored) ? stored : (stored ? [stored] : []);
    if (!list.length) return undefined;
    return list.map((f, i) => {
        if (typeof f === 'string') {
            return { uid: `${field.field}-${i}`, name: f, status: 'done' };
        }
        const rawUrl = f.location ?? f.url ?? f.path ?? f.documentCopyLocation ?? '';
        const fullUrl = rawUrl && !String(rawUrl).startsWith('http')
            ? `${JOBS_URL}/${rawUrl}`
            : rawUrl;
        return {
            uid: String(f.id ?? f._id ?? f.uniqueName ?? f.documentCopyUniqueName ?? `${field.field}-${i}`),
            name: f.name ?? f.uploadName ?? f.uploadedFileName ?? f.documentUploadName ?? 'file',
            status: 'done',
            url: fullUrl || undefined,
        };
    });
}

// buildRowFileList — turn an addRow row's stored file metadata into the antd
// fileList. Inside a row the original filename lives at the file field's own key
// (e.g. documentUploadName) and the s3 path in a sibling key, so we read the name
// from the field key and the location from field.locationKey (or the first sibling
// key that looks like a path/url). Returns undefined when the row has no file.
function buildRowFileList(row, field) {
    if (!row || typeof row !== 'object') return undefined;
    const name = getDeep(row, field.field);
    const locKey = field.locationKey
        ?? Object.keys(row).find((k) => /location|url|path/i.test(k) && row[k]);
    const rawUrl = locKey ? row[locKey] : '';
    if (!name && !rawUrl) return undefined;
    const fullUrl = rawUrl && !String(rawUrl).startsWith('http')
        ? `${JOBS_URL}/${rawUrl}`
        : rawUrl;
    const uid = row.documentCopyUniqueName ?? row.uniqueName ?? name ?? `${field.field}-0`;
    return [{
        uid: String(uid),
        name: name ?? 'file',
        status: 'done',
        url: fullUrl || undefined,
    }];
}

// buildAddRowInitial — turn a repeatable (addRow) group's backend-embedded stored
// rows into the Form.List initialValue: an array with ONE object per stored row,
// each mapped through buildInitialValue (file fields via the row's sibling keys).
//
// Why initialValue and not setFieldsValue: antd's Form.List only reliably
// materialises N pre-filled rows when the values are supplied as the list's
// initialValue at mount. Calling form.setFieldsValue([...]) on a Form.List that
// mounted empty reconciles by row key and ends up keeping only the LAST row's
// values (every earlier row renders blank) — exactly the multi-record edit bug.
// Returns [] when the group has no stored rows (so the user still gets the
// "Add Row" affordance with no phantom blank row).
function buildAddRowInitial(group) {
    const storedRows = Array.isArray(group.rows) ? group.rows : null;
    const mapRow = (row, readFile, readScalar) => {
        const rowVals = {};
        (group.fields ?? []).forEach((field) => {
            if (!field.field) return;
            if (field.type === 'file') {
                const fileList = readFile(row, field);
                if (fileList) rowVals[field.field] = fileList;
                return;
            }
            const stored = readScalar(row, field);
            if (stored === undefined || stored === null) return;
            rowVals[field.field] = buildInitialValue(field, stored);
        });
        return rowVals;
    };

    if (storedRows && storedRows.length > 0) {
        return storedRows.map((row) => mapRow(
            row,
            (r, field) => buildRowFileList(r, field),
            (r, field) => getDeep(r, field.field),
        ));
    }

    // Fallback for an older backend that embeds only per-field field.value (row 0).
    const rowVals = mapRow(
        group,
        (_g, field) => fileListFromValue(field.value, field),
        (_g, field) => field.value,
    );
    return Object.keys(rowVals).length > 0 ? [rowVals] : [];
}

function stripHtml(html) {
    return String(html ?? '')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .trim();
}

function getValidationValue(field, type) {
    const rules = field.validations ?? field.validation ?? field.rules ?? [];
    const rule = rules.find((r) => (typeof r === 'string' ? r : r?.type) === type);
    if (!rule || typeof rule === 'string') return undefined;
    return rule.value ?? rule.length ?? rule[type];
}

const numberRangeRule = ({ label, message: msg, value, compare, text }) => ({
    validator: (_, input) => {
        if (empty(input)) return Promise.resolve();
        return compare(Number(input), Number(value))
            ? Promise.resolve()
            : Promise.reject(new Error(msg ?? `${label} ${text} ${value}`));
    },
});

const validationRulesByType = {
    required: ({ field, message: msg }) => ({
        validator: (_, value) => {
            let isEmpty =
                value === undefined || value === null || value === '' ||
                (Array.isArray(value) && value.length === 0);
            if (!isEmpty && field?.type === 'text-editor') isEmpty = stripHtml(value) === '';
            return isEmpty
                ? Promise.reject(new Error(msg ?? 'Mandatory Field'))
                : Promise.resolve();
        },
    }),
    email: ({ label, message: msg }) => ({ type: 'email', message: msg ?? `Enter valid ${label}` }),
    url: ({ label, message: msg }) => ({ type: 'url', message: msg ?? `Enter valid ${label}` }),
    number: ({ label, message: msg }) => ({ pattern: /^[0-9]+$/, message: msg ?? `${label} must be a number` }),
    phone: ({ message: msg }) => ({ pattern: /^[0-9]{10}$/, message: msg ?? 'Phone number must contain exactly 10 digits' }),
    len: ({ label, message: msg, value }) => ({ len: Number(value), message: msg ?? `${label} must be ${value} characters` }),
    pattern: ({ label, message: msg, rule }) => ({
        pattern: new RegExp(rule.pattern ?? rule.value, rule.flags),
        message: msg ?? `Enter valid ${label}`,
    }),
    min: ({ field, label, message: msg, value }) =>
        field.type === 'number'
            ? numberRangeRule({ label, message: msg, value, compare: (i, m) => i >= m, text: 'must be at least' })
            : {
                validator: (_, input) => {
                    if (input == null || input === '') return Promise.resolve();
                    const content = field.type === 'text-editor' ? stripHtml(input) : String(input);
                    if (content === '') return Promise.resolve();
                    return content.length >= Number(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error(msg ?? `${label} must be at least ${value} characters`));
                },
            },
    max: ({ field, label, message: msg, value }) =>
        field.type === 'number'
            ? numberRangeRule({ label, message: msg, value, compare: (i, m) => i <= m, text: 'must not exceed' })
            : {
                validator: (_, input) => {
                    if (input == null || input === '') return Promise.resolve();
                    const content = field.type === 'text-editor' ? stripHtml(input) : String(input);
                    if (content === '') return Promise.resolve();
                    return content.length <= Number(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error(msg ?? `${label} must not exceed ${value} characters`));
                },
            },
};

function validationToRule(validation, field) {
    const rule = typeof validation === 'string' ? { type: validation } : validation;
    const type = rule.type ?? rule.rule ?? rule.name;
    const value = rule.value ?? rule.length ?? rule.min ?? rule.max;
    const label = field.label ?? field.field ?? 'Field';
    const factory = validationRulesByType[type];
    return factory ? factory({ field, label, message: rule.message, rule, value }) : rule;
}

function getRules(field) {
    const label = field.label ?? field.field ?? 'Field';
    const apiRules = field.validations ?? field.validation ?? field.rules ?? [];
    const hasApiRequired = apiRules.some((r) => (typeof r === 'string' ? r : r?.type) === 'required');
    const hasApiEmail = apiRules.some((r) => (typeof r === 'string' ? r : r?.type) === 'email');
    const rules = [];
    if (truthy(field.req) && !hasApiRequired) rules.push(validationRulesByType.required({ field, message: 'Mandatory Field' }));
    if (field.type === 'email' && !hasApiEmail) rules.push({ type: 'email', message: `Enter valid ${label}` });
    if (field.type === 'number') rules.push({ pattern: /^[0-9]+$/, message: `${label} must be a number` });
    return [...rules, ...apiRules.map((r) => validationToRule(r, field))];
}

function getInputLimits(field) {
    const len = field.len ?? getValidationValue(field, 'len');
    const min = field.min ?? getValidationValue(field, 'min');
    const max = field.max ?? getValidationValue(field, 'max');
    if (field.type === 'number') return { min: min ?? undefined, max: max ?? undefined };
    return { minLength: min ?? undefined, maxLength: field.maxLength ?? len ?? max ?? undefined };
}

function cleanValue(value, formatter) {
    if (!formatter) return value;
    if (formatter === 'phone' || formatter === 'digits') return value.replace(/\D/g, '').slice(0, 10);
    if (formatter === 'email' || formatter === 'noSpaces') return value.replace(/\s/g, '');
    if (formatter === 'name') return value.replace(/\s+/g, ' ').trimStart().replace(/\b\w/g, (c) => c.toUpperCase());
    return value;
}

// ─── FieldControl ─────────────────────────────────────────────────────────────

function FieldControl({ field, form, name, value, checked, fileList, moduleName, initialValue }) {
    const type = field.type ?? 'text';
    const disabled = !editable(field);

    // ALL hooks at the top — before any conditional returns
    const [dynamicOptions, setDynamicOptions] = useState(null);
    const [optionsLoading, setOptionsLoading] = useState(false);

    useEffect(() => {
        const ds = field.dataSource;
        if (!['select', 'radio', 'checkbox'].includes(type)) return undefined;
        if (!ds || ds === 'static') return undefined;

        let cancelled = false;

        async function fetchOptions() {
            try {
                setOptionsLoading(true);
                let raw = [];

                if (ds === 'master' || ds === 'module') {
                    raw = await getDropdownValues(moduleName, field.field);
                } else if (ds === 'lookup') {
                    const params = new URLSearchParams({
                        collection: field.lookupCollection ?? '',
                        displayField: field.displayField ?? '',
                        valueField: field.valueField ?? '_id',
                    });
                    if (field.displayField2) params.set('displayField2', field.displayField2);
                    const json = await fetchJsonWithAuth(AUTH_URL, `/admin/lookup-dropdown-values?${params}`);
                    raw = json?.data ?? json ?? [];
                } else if (ds === 'group') {
                    const groupName = String(field.groupName ?? '').toLowerCase().trim();
                    if (groupName === 'users' || groupName === 'user') {
                        const json = await fetchJsonWithAuth(AUTH_URL, '/get-all-users?limit=500&offset=0');
                        const users = json?.data?.users ?? json?.users ?? json?.data ?? [];
                        raw = Array.isArray(users)
                            ? users.map((u) => ({
                                label: [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
                                    || u.userName || u.email || String(u.userId ?? u.id),
                                value: u.userId ?? u.id,
                            }))
                            : [];
                    }
                }

                if (!cancelled) setDynamicOptions(normalizeOptions(Array.isArray(raw) ? raw : []));
            } catch (err) {
                console.error(`[EditFormV1] Failed to fetch options for "${field.field}":`, err);
                if (!cancelled) setDynamicOptions([]);
            } finally {
                if (!cancelled) setOptionsLoading(false);
            }
        }

        fetchOptions();
        return () => { cancelled = true; };
    }, [type, field.dataSource, field.field, field.lookupCollection,
        field.displayField, field.displayField2, field.valueField, field.groupName, moduleName]);

    // ── Derived values ──
    const configuredOptions = [field.options, field.values]
        .find((items) => Array.isArray(items) && items.length > 0)
        ?? getEditFormFallbackOptions(field);
    const staticOptions = normalizeOptions(configuredOptions);
    const options = dynamicOptions ?? staticOptions;

    const selectMode = field.multiSelect
        ? 'multiple'
        : field.mode ?? (truthy(field.multiple) ? 'multiple' : undefined);
    const inferredSelectMode = selectMode ?? (effectiveDataType(field) === 'array' ? 'multiple' : undefined);

    const placeholder = field.placeholder ??
        `${type === 'select' ? 'Select' : 'Enter'} ${String(field.label ?? '').toLowerCase()}`;
    const inputLimits = getInputLimits(field);

    const validateField = () => {
        setTimeout(() => { form.validateFields([name]).catch(() => { }); });
    };

    const updateAndValidate = (nextValue) => {
        form.setFieldValue(name, nextValue);
        validateField();
    };

    const inputProps = {
        ...inputLimits,
        value,
        disabled,
        placeholder,
        prefix: field.icon ? null : fieldIcons[field.field],
        onChange: (e) => {
            const next = field.formatter
                ? cleanValue(e.target.value, field.formatter)
                : e.target.value;
            updateAndValidate(next);
        },
    };

    // ── Renders ──

    if (type === 'tagify') {
        return (
            <Select value={value} allowClear disabled={disabled} mode="tags"
                placeholder={placeholder} onChange={updateAndValidate} />
        );
    }

    if (type === 'select') {
        return (
            <Select value={value} allowClear disabled={disabled} loading={optionsLoading}
                mode={inferredSelectMode} options={options} placeholder={placeholder}
                notFoundContent={optionsLoading ? <Spin size="small" /> : undefined}
                onChange={updateAndValidate} />
        );
    }

    if (type === 'radio') {
        return (
            <Radio.Group value={value} disabled={disabled} options={options}
                onChange={(e) => updateAndValidate(e.target.value)} />
        );
    }

    if (type === 'checkbox') {
        if (options.length > 0) {
            return (
                <Checkbox.Group value={value} disabled={disabled} options={options}
                    onChange={updateAndValidate} />
            );
        }
        return (
            <Checkbox checked={checked} disabled={disabled}
                onChange={(e) => updateAndValidate(e.target.checked)}>
                {field.checkboxLabel ?? field.label}
            </Checkbox>
        );
    }

    if (type === 'text-editor') {
        return (
            <TipTapEditor
                value={initialValue ?? value ?? ''}
                disabled={disabled}
                onChange={(html) => updateAndValidate(html)}
            />
        );
    }

    if (type === 'textarea') {
        return <TextArea {...inputProps} rows={field.rows ?? 3} />;
    }

    if (type === 'date') {
        console.log('DATE FIELD name:', name, 'value:', value);
        return (
            <DatePicker value={value} disabled={disabled} placeholder={placeholder}
                style={{ width: '100%' }} suffixIcon={<CalendarOutlined />}
                onChange={(date) => {
                    console.log('DATE PICKED:', date);
                    updateAndValidate(date);
                }} />
        );
    }

    if (type === 'time') {
        return (
            <TimePicker value={value} disabled={disabled} placeholder={placeholder}
                style={{ width: '100%' }} onChange={(time) => updateAndValidate(time)} />
        );
    }

    if (type === 'file') {
        const fileRules = field.validations ?? field.validation ?? field.rules ?? [];
        const fileTypeRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'fileType');
        const fileSizeRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'fileSize');
        const maxFilesRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'maxFiles');

        const acceptTypes = fileTypeRule?.value
            ? fileTypeRule.value.split(',').map((e) => `.${e.trim().replace(/^\./, '')}`).join(',')
            : undefined;
        const maxSizeMB = fileSizeRule ? Number(fileSizeRule?.value ?? 5) : undefined;
        // maxFiles validation rule takes top priority; otherwise use field.multiple/maxCount
        // (set via admin panel toggle), falling back to single-file mode.
        const maxFiles = maxFilesRule
            ? Number(maxFilesRule?.value ?? 1)
            : (truthy(field.multiple) ? (field.maxCount ?? 5) : 1);

        const fileList = Array.isArray(value) ? value : [];
        const canAddMore = fileList.length < maxFiles;

        const formatFileSize = (bytes) => {
            if (!bytes) return '';
            if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
            return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
        };

        const getFileIcon = (fileName) => {
            const ext = (fileName ?? '').split('.').pop()?.toLowerCase();
            if (ext === 'pdf') return <FilePdfOutlined className="v1-file-type-icon v1-file-icon-pdf" />;
            if (['doc', 'docx'].includes(ext)) return <FileWordOutlined className="v1-file-type-icon v1-file-icon-word" />;
            if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileExcelOutlined className="v1-file-type-icon v1-file-icon-excel" />;
            if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return <FileImageOutlined className="v1-file-type-icon v1-file-icon-image" />;
            return <FileOutlined className="v1-file-type-icon v1-file-icon-default" />;
        };

        const handleFileDelete = (index) => {
            updateAndValidate(fileList.filter((_, i) => i !== index));
        };

        const handleFileView = (file) => {
            // New file — create blob URL
            if (file.originFileObj instanceof File) {
                window.open(URL.createObjectURL(file.originFileObj), '_blank');
                return;
            }
            // Existing file — prepend backend URL if relative
            const rawUrl = file.url ?? file.thumbUrl;
            if (!rawUrl) return;
            const fullUrl = rawUrl.startsWith('http')
                ? rawUrl
                : `${JOBS_URL}/${rawUrl}`;
            window.open(fullUrl, '_blank');
        };

        const isMulti = maxFiles > 1;
        const remaining = maxFiles - fileList.length;

        const typeHint = acceptTypes
            ? acceptTypes.split(',').map((t) => t.replace('.', '').toUpperCase()).join(', ')
            : null;
        const sizeHint = maxSizeMB ? `up to ${maxSizeMB} MB` : null;
        const baseHint = [typeHint, sizeHint].filter(Boolean).join(' ');
        const dropHint = isMulti && fileList.length > 0
            ? [baseHint, `${remaining} of ${maxFiles} remaining`].filter(Boolean).join(' • ')
            : baseHint;
        const dropTitle = fileList.length > 0
            ? `Add ${isMulti ? 'more files' : 'file'}`
            : (field.buttonLabel ?? `Upload ${field.label ?? 'File'}`);

        const beforeUploadHandler = (file) => {
            if (fileList.length >= maxFiles) return Upload.LIST_IGNORE;
            if (acceptTypes) {
                const ext = (file.name.split('.').pop() ?? '').toLowerCase();
                const allowed = acceptTypes.split(',').map((t) => t.replace(/^\./, '').toLowerCase());
                if (!allowed.includes(ext)) {
                    message.error(fileTypeRule?.message ?? `Only ${fileTypeRule.value} files are allowed`);
                    return Upload.LIST_IGNORE;
                }
            }
            if (maxSizeMB !== undefined && file.size / 1024 / 1024 > maxSizeMB) {
                message.error(fileSizeRule?.message ?? `File must be smaller than ${maxSizeMB} MB`);
                return Upload.LIST_IGNORE;
            }
            return false;
        };

        // antd calls onChange once per file even in multi-select, each time with the
        // full accumulated newList. Slicing to maxFiles guards against racing additions.
        const handleUploadChange = ({ fileList: newList }) => {
            updateAndValidate(newList.slice(0, maxFiles));
        };

        const multiUploadProps = {
            accept: acceptTypes,
            fileList,
            showUploadList: false,
            multiple: true,
            maxCount: maxFiles,
            beforeUpload: beforeUploadHandler,
            onChange: handleUploadChange,
            disabled,
        };

        const fileRows = fileList.map((file, index) => (
            <div key={file.uid ?? index} className="v1-file-item">
                <div className="v1-file-item-icon">{getFileIcon(file.name)}</div>
                <div className="v1-file-item-info">
                    <span className="v1-file-item-name">{file.name}</span>
                    <span className="v1-file-item-meta">
                        {formatFileSize(file.size)}{file.size ? ' • ' : ''}Uploaded just now
                    </span>
                </div>
                <Space size={6} className="v1-file-item-actions">
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleFileView(file)}
                        className="v1-file-view-btn"
                    >
                        View
                    </Button>
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleFileDelete(index)}
                        className="v1-file-delete-btn"
                    />
                </Space>
            </div>
        ));

        if (isMulti) {
            const multiTitle = fileList.length > 0
                ? `Upload more ${field.label ?? 'files'}`
                : (field.buttonLabel ?? `Upload ${field.label ?? 'File'}`);

            return (
                <div className="v1-file-upload-wrapper">
                    {/* Centered dropzone — always at top in multiple mode */}
                    {canAddMore && (
                        <Upload {...multiUploadProps} style={{ width: '100%', display: 'block' }}>
                            <div className={`v1-upload-dropzone v1-upload-dropzone-center${disabled ? ' v1-upload-dropzone-disabled' : ''}`}>
                                <div className="v1-upload-dropzone-title-row">
                                    <CloudUploadOutlined className="v1-upload-cloud-icon v1-upload-cloud-icon-sm" />
                                    <span className="v1-upload-dropzone-title v1-upload-dropzone-title-blue">{multiTitle}</span>
                                </div>
                                <span className="v1-upload-dropzone-sub">Drag & drop or click to upload</span>
                                {baseHint && <span className="v1-upload-dropzone-sub">{baseHint}</span>}
                            </div>
                        </Upload>
                    )}

                    {/* Uploaded file rows — 2-column grid */}
                    {fileList.length > 0 && (
                        <div className="v1-file-grid">
                            {fileRows}
                        </div>
                    )}

                    {/* "+ Add more files" text link at the bottom */}
                    {canAddMore && fileList.length > 0 && (
                        <Upload {...multiUploadProps} style={{ display: 'inline-block' }}>
                            <Button type="link" size="small" icon={<PlusOutlined />} className="v1-add-more-files-btn">
                                Add more files
                            </Button>
                        </Upload>
                    )}
                </div>
            );
        }

        return (
            <div className="v1-file-upload-wrapper">
                {/* Single mode: compact file row at field height */}
                {fileList.length > 0 && <div className="v1-file-rows-single">{fileRows}</div>}

                {canAddMore && (
                    <Upload
                        accept={acceptTypes}
                        fileList={fileList}
                        showUploadList={false}
                        multiple={false}
                        maxCount={1}
                        beforeUpload={beforeUploadHandler}
                        onChange={handleUploadChange}
                        disabled={disabled}
                        style={{ width: '100%', display: 'block' }}
                    >
                        <div className={`v1-upload-dropzone v1-upload-dropzone-single${disabled ? ' v1-upload-dropzone-disabled' : ''}`}>
                            <CloudUploadOutlined className="v1-upload-cloud-icon" />
                            <span className="v1-upload-dropzone-title">{dropTitle}</span>
                            {dropHint && <span className="v1-upload-dropzone-sub">— {dropHint}</span>}
                        </div>
                    </Upload>
                )}
            </div>
        );
    }

    return <Input {...inputProps} type={type === 'number' ? 'number' : type} />;
}

// ─── SectionTitle ─────────────────────────────────────────────────────────────

function SectionTitle({ icon, title }) {
    return (
        <Space size={10} align="center">
            <div className="v1-section-icon-box">{icon}</div>
            <Text strong className="v1-section-label">{title}</Text>
        </Space>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function EditFormV1({
    moduleName,
    recordId,
    clientId,
    region,
    breadcrumbItems = [],
    submitText = 'Save Changes',
    cancelPath,
    onSuccess,
    // fieldNameMap: rename keys before sending to API
    // e.g. { mspReqId: 'mspreqId' }
    // Once backend standardises field names this prop can be removed
    fieldNameMap = {},
    // onFileUpload: (fieldName, file, recordId) => Promise
    // Handle multipart file uploads per module outside this component
    onFileUpload = null,
}) {
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const [collapsed, setCollapsed] = useState({});
    const [formGroups, setFormGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const groups = useMemo(() => normalizeGroups(formGroups), [formGroups]);

    // Per-addRow-group Form.List initialValue (one row object per backend-embedded
    // stored row). Computed from the same groups the form renders, so it is ready
    // the moment each Form.List mounts.
    const addRowInitials = useMemo(() => {
        const out = {};
        groups.forEach((group) => {
            if (group.addRow) out[group.name] = buildAddRowInitial(group);
        });
        return out;
    }, [groups]);

    const MODULE_LABELS = {
        jobs: { list: 'Jobs', path: '/jobs', edit: 'Edit Job' },
        candidates: { list: 'Candidates', path: '/candidates', edit: 'Edit Candidate' },
        submissions: { list: 'Submissions', path: '/submissions', edit: 'Edit Submission' },
    };

    const resolvedBreadcrumbs = breadcrumbItems.length > 0
        ? breadcrumbItems
        : (() => {
            const config = MODULE_LABELS[normalizeModule(moduleName)];
            if (!config) return [];
            return [
                { label: 'Home', href: '/' },
                { label: config.list, href: config.path },
                { label: config.edit },
            ];
        })();


    // ── Fetch form config + record ──────────────────────────────────────────

    useEffect(() => {
        if (!moduleName || !recordId) return undefined;

        let cancelled = false;

        async function fetchData() {
            try {
                setLoading(true);
                setError('');
                setSubmitError('');

                // Single source of truth: the form-groups config with `id` embeds
                // each field's current value (field.value). No separate detail-view
                // fetch — prefill reads field.value only.
                const groupsResponse = await getFormGroups({ module: moduleName, id: recordId, clientId, region });

                if (cancelled) return;

                setFormGroups(Array.isArray(groupsResponse) ? groupsResponse : []);
            } catch (err) {
                console.error(`Failed to fetch ${moduleName} edit data:`, err);
                if (!cancelled) setError(err?.message || 'Failed to load record');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        fetchData();
        return () => { cancelled = true; };
    }, [moduleName, recordId, clientId, region]);

    // ── Prefill form ────────────────────────────────────────────────────────
    //
    // Single source of truth: each field's `field.value`, embedded by getFormGroups
    // when an `id` is passed. No separate detail-view fetch. The backend resolves
    // references to { id, value } and file fields to the stored file object, which
    // buildInitialValue / fileListFromValue convert to the control's value.

    useEffect(() => {
        if (loading || groups.length === 0) return;

        const values = {};

        groups.forEach((group) => {
            // Repeatable (addRow) groups are prefilled via Form.List `initialValue`
            // (see addRowInitials / renderGroup), NOT here — setFieldsValue on a
            // Form.List that mounted empty only keeps the last row. Skip them.
            if (group.addRow) return;

            (group.fields ?? []).forEach((field) => {
                if (!field.field) return;

                // Single source of truth: field.value, embedded by getFormGroups.
                const stored = field.value;
                const name = field.field;

                // ── File fields — convert the embedded file value to antd fileList ──
                if (field.type === 'file') {
                    const fileList = fileListFromValue(stored, field);
                    if (fileList) values[name] = fileList;
                    return;
                }

                if (stored === undefined || stored === null) return;

                // ── Checkbox group with a value map — reverse the transformRule ──
                // so a stored "high" re-checks the box whose mapped value is "high".
                if (field.type === 'checkbox' && Array.isArray(field.options) && field.options.length > 0) {
                    const map = field.transformRule?.map;
                    if (map) {
                        const match = Object.entries(map).find(([, to]) => String(to) === String(stored));
                        values[name] = match ? [match[0]] : [];
                    } else {
                        values[name] = Array.isArray(stored) ? stored : [stored];
                    }
                    return;
                }

                // ── Everything else → the config-driven engine ──
                values[name] = buildInitialValue(field, stored);
            });
        });

        if (Object.keys(values).length > 0) {
            form.setFieldsValue(values);
        }
    }, [loading, groups, form]);

    // ── Collapse toggle ─────────────────────────────────────────────────────

    const toggle = (key) => {
        setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    // ── Payload builder — the SAME config-driven engine AddFormV1 uses ──────────
    //
    // No field-name / module-specific logic. The engine:
    //   • starts from the untouched record (base) so unedited fields are preserved
    //   • applies each field's payloadKey / dataType / payloadMode / template
    //   • skips file fields (uploaded separately via onFileUpload)
    //
    // fieldNameMap is an optional caller-provided rename map kept for backward
    // compatibility; new renames should use the field's payloadKey config instead.
    function buildPayload(values) {
        // No base record: every editable value comes from the form (prefilled from
        // getFormGroups field.value). The payload carries only the configured fields.
        const payload = buildConfigPayload(values, groups);

        Object.entries(fieldNameMap).forEach(([from, to]) => {
            if (payload[from] !== undefined) {
                payload[to] = payload[from];
                delete payload[from];
            }
        });

        return payload;
    }

    // ── Submit ──────────────────────────────────────────────────────────────

    async function submit(values) {
        try {
            setSubmitting(true);
            setSubmitError('');

            // New files ride along in the SAME module/update multipart, exactly
            // like create — config-driven fileKey (jobs → "file", candidates →
            // resume/passport/documents/…). The downstream update handler stores
            // them and returns name/location. This replaces the old per-module
            // onFileUpload endpoints (which pointed candidate edits at the jobs
            // attachment URL). onFileUpload is still honoured as an optional
            // override when a module needs bespoke upload handling.
            // Indexed keys (documents[0], documents[1], …) so the update handler
            // can align each new file to its metadata row by position. Create uses
            // flat keys; only update needs indexing.
            const fileParts = collectFileParts(values, groups, { indexed: true });

            if (onFileUpload && onFileUpload.useLegacyUpload) {
                const fileFieldDefs = groups
                    .flatMap((g) => g.fields ?? [])
                    .filter((f) => f.type === 'file');
                for (const field of fileFieldDefs) {
                    const list = values[field.field];
                    if (!Array.isArray(list)) continue;
                    const newFiles = list.filter((f) => f.originFileObj instanceof File);
                    try {
                        await Promise.all(
                            newFiles.map((f) => onFileUpload(field.field, f.originFileObj, recordId)),
                        );
                    } catch (uploadErr) {
                        console.warn('File upload failed — continuing with save:', uploadErr);
                    }
                }
            }

            // Save JSON payload + new files together through the gateway.
            await updateRecord(moduleName, recordId, buildPayload(values),
                onFileUpload?.useLegacyUpload ? [] : fileParts);

            message.success(`${moduleName} updated successfully`);

            if (onSuccess) {
                onSuccess();
            } else if (cancelPath) {
                navigate(cancelPath);
            } else {
                navigate(-1);
            }
        } catch (err) {
            console.error(`Failed to update ${moduleName}:`, err);
            setSubmitError(err?.message || 'Failed to save changes');
            message.error(err?.message || 'Failed to save changes');
        } finally {
            setSubmitting(false);
        }
    }

    // ── Scroll to first error on validation fail ────────────────────────────

    const handleSubmitFailed = ({ errorFields }) => {
        if (!errorFields?.length) return;
        const errorFormItem = document.querySelector('.ant-form-item-has-error');
        if (!errorFormItem) return;
        const input = errorFormItem.querySelector('input, textarea, select, .ProseMirror');
        if (!input) return;
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
            input.focus?.();
            input.style.boxShadow = '0 0 0 3px rgba(255, 77, 79, 0.3)';
            setTimeout(() => { input.style.boxShadow = ''; }, 1000);
        }, 300);
    };

    // ── Render ──────────────────────────────────────────────────────────────

    const [combineToggles, setCombineToggles] = useState({});
    const fieldKey = (field) => String(field?.field ?? '');

    const renderField = (field, namePrefix, listName, opts = {}) => {
        // Inside a Form.List, namePrefix is the row index — which is 0 for the
        // FIRST row. A truthy check (`namePrefix ?`) treats 0 as "no prefix" and
        // registers row 0's fields at the form root, so the first row never
        // prefills and its edits write to the wrong path. Use an explicit
        // undefined check (matching AddFormV1).
        const name = namePrefix !== undefined ? [namePrefix, field.field] : field.field;
        // Form.Item registers with the RELATIVE name inside Form.List, but
        // form.setFieldValue / validateFields (called from FieldControl) need the
        // ABSOLUTE path [listName, rowIndex, field] to hit the right store slot.
        const absoluteName = (listName !== undefined && namePrefix !== undefined)
            ? [listName, namePrefix, field.field]
            : name;
        const isCheckboxGroup =
            field.type === 'checkbox' &&
            ((field.options && field.options.length > 0) ||
                (field.dataSource && field.dataSource !== 'static'));

        const valuePropName = !isCheckboxGroup && field.type === 'checkbox'
            ? 'checked'
            : 'value';

        // Inline (combined) fields show no per-box label — the combined card prints a
        // single label and the boxes sit side by side (e.g. From / To / Currency).
        const fieldLabel = opts.inline
            ? ''
            : !isCheckboxGroup && field.type === 'checkbox'
            ? ''
            : <FieldLabel field={field} label={field.label} />;

        const mdSpan = opts.inline
            ? 24
            : field.type === 'text-editor' || (field.type === 'file' && truthy(field.multiple))
            ? 24
            : (field.col ?? 12);

        // Inside a combined block, each box's width follows its combineWidth weight
        // (flex-grow), so the pack can be sized by ratio (e.g. 2:1, 1:2:3). Unset → 1.
        const inlineStyle = opts.inline
            ? { flex: `${Number(field.combineWidth) > 0 ? Number(field.combineWidth) : 1} 1 0`, maxWidth: 'none' }
            : undefined;

        return (
            <Col key={field.field} xs={24} md={mdSpan} style={inlineStyle} className={opts.inline ? 'v1-combine-cell' : undefined}>
                <Form.Item
                    label={fieldLabel}
                    name={name}
                    rules={getRules(field)}
                    valuePropName={valuePropName}
                    getValueFromEvent={field.type === 'file' ? (e) => Array.isArray(e) ? e : e?.fileList : undefined}
                    validateTrigger={['onBlur', 'onChange']}
                >
                    <FieldControl
                        field={field}
                        form={form}
                        name={absoluteName}
                        moduleName={moduleName}
                        initialValue={field.value}
                    />
                </Form.Item>
            </Col>
        );
    };

    const renderFields = (fields, namePrefix, listName) => {
        const used = new Set();
        const out = [];
        fields.forEach((field) => {
            const combineKey = field.combineKey?.trim?.();
            if (!combineKey) {
                out.push(renderField(field, namePrefix, listName));
                return;
            }
            const packKey = `${listName ?? 'root'}:${namePrefix ?? 'root'}:${combineKey}`;
            if (used.has(packKey)) return;
            const pack = fields.filter((f) => f.combineKey === combineKey);
            pack.forEach((f) => used.add(`${listName ?? 'root'}:${namePrefix ?? 'root'}:${f.combineKey}`));

            const label = pack.find((f) => f.combineLabel)?.combineLabel ?? field.combineLabel ?? field.label;
            const toggleTarget = pack.find((f) => f.combineToggleTarget)?.combineToggleTarget;
            const toggleLabel = pack.find((f) => f.combineToggleLabel)?.combineToggleLabel ?? 'Show range';
            const targetField = toggleTarget ? pack.find((f) => fieldKey(f) === toggleTarget) : null;
            const targetName = targetField
                ? ((listName !== undefined && namePrefix !== undefined) ? [listName, namePrefix, targetField.field] : targetField.field)
                : null;

            // One label, boxes inline on a single row, range toggle BELOW. Toggle off →
            // only the primary (target hidden); on → the target box appears alongside.
            const primaryReq = pack.some((f) => truthy(f.req));
            const comboSpan = pack.length >= 3 ? 24 : 12;
            out.push(
                <Col key={packKey} xs={24} md={comboSpan}>
                    <Form.Item label={label} required={primaryReq} colon={false} className="v1-combine-field">
                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                const hasTargetValue = targetName ? !empty(getFieldValue(targetName)) : false;
                                const checked = combineToggles[packKey] ?? hasTargetValue;
                                const visiblePack = toggleTarget && !checked
                                    ? pack.filter((f) => fieldKey(f) !== toggleTarget)
                                    : pack;
                                return (
                                    <>
                                        <Row gutter={[8, 0]} align="top" wrap={false} className="v1-combine-row">
                                            {visiblePack.map((f) => renderField(f, namePrefix, listName, { inline: true }))}
                                        </Row>
                                        {toggleTarget && (
                                            <div className="v1-combine-range">
                                                <Switch
                                                    size="small"
                                                    checked={checked}
                                                    onChange={(next) => setCombineToggles((prev) => ({ ...prev, [packKey]: next }))}
                                                />
                                                <Text type="secondary" className="v1-combine-toggle-label">{toggleLabel}</Text>
                                            </div>
                                        )}
                                    </>
                                );
                            }}
                        </Form.Item>
                    </Form.Item>
                </Col>,
            );
        });
        return out;
    };

    const renderGroup = (group, index) => {
        const key = group.name ?? group.id ?? String(index);

        const body = group.addRow ? (
            <Form.List name={group.name ?? key} initialValue={addRowInitials[group.name] ?? []}>
                {(rows, { add, remove }) => (
                    <>
                        <Button type="link" icon={<PlusOutlined />} onClick={() => add()}>
                            {group.addLabel ?? `Add ${group.label ?? 'Row'}`}
                        </Button>
                        {rows.map(({ key: rowKey, name }) => (
                            <Row key={rowKey} gutter={[24, 0]} align="top">
                                {renderFields(group.fields, name, group.name ?? key)}
                                <Col xs={24} md={2}>
                                    <Button type="text" danger icon={<DeleteOutlined />}
                                        onClick={() => remove(name)} />
                                </Col>
                            </Row>
                        ))}
                    </>
                )}
            </Form.List>
        ) : (
            <Row gutter={[24, 0]}>{renderFields(group.fields)}</Row>
        );

        return (
            <Card
                key={key}
                className="v1-card v1-edit-card"
                title={
                    <SectionTitle
                        icon={sectionIcons[index % sectionIcons.length]}
                        title={group.label ?? group.name ?? 'Details'}
                    />
                }
                extra={
                    <Space size={8} align="center" className="v1-group-actions">
                        <GroupInfoMessage group={group} />
                        <Button type="text" size="small"
                            icon={collapsed[key] ? <DownOutlined /> : <UpOutlined />}
                            onClick={(e) => { e.stopPropagation(); toggle(key); }}
                        />
                    </Space>
                }
            >
                <div style={{ display: collapsed[key] ? 'none' : 'block' }}>{body}</div>
            </Card>
        );
    };

    return (
        <main>
            {resolvedBreadcrumbs.length > 0 && <AppBreadcrumb items={resolvedBreadcrumbs} />}

            <div className="v1-edit-page">
                <div className="v1-page-inner">
                    <Spin spinning={loading} tip="Loading form fields...">
                        {error && (
                            <Alert type="error" showIcon message={error}
                                style={{ marginBottom: 16 }} />
                        )}
                        {submitError && (
                            <Alert type="error" showIcon message={submitError}
                                style={{ marginBottom: 16 }} />
                        )}

                        <Form
                            form={form}
                            layout="vertical"
                            colon={false}
                            className="v1-form"
                            requiredMark={(label, { required }) => (
                                <>
                                    {label}
                                    {required && <span className="v1-required-mark"> *</span>}
                                </>
                            )}
                            onFinish={submit}
                            onFinishFailed={handleSubmitFailed}
                        >
                            {groups.map(renderGroup)}

                            {!loading && !error && groups.length === 0 && (
                                <Alert type="info" showIcon message="No form fields are configured." />
                            )}
                        </Form>
                    </Spin>
                </div>

                <div className="v1-footer">
                    <Text type="secondary">
                        All unsaved changes will be lost if you cancel and reload.
                    </Text>
                    <Space size={12}>
                        <Button onClick={() => (cancelPath ? navigate(cancelPath) : navigate(-1))}>
                            Cancel
                        </Button>
                        <Button type="primary" onClick={() => form.submit()}
                            disabled={loading || submitting} loading={submitting}>
                            {submitText}
                        </Button>
                    </Space>
                </div>
            </div>
        </main>
    );
}
